import React, { forwardRef } from 'react';
import * as DataListPrimitive from '@radix-ui/themes/components/data-list';
import type {
  RadixDataListItemProps,
  RadixDataListLabelProps,
  RadixDataListRootProps,
  RadixDataListValueProps,
} from '@/types/radix-ui';
import clsx from 'clsx';
import type { SeparatorProps } from './Separator';
import { Separator } from './Separator';

/**
 * DataList component - enhanced wrapper around Radix UI DataList
 * Provides both simple API with props and advanced API with subcomponents
 */

export type DataListSize = 'xsmall' | 'small' | 'medium' | 'large';
export type DataListValueAlignment = 'start' | 'end';

interface BaseDataListRootProps {
  size?: DataListSize;
  alignValues?: DataListValueAlignment;
}

export type DataListRootProps = Omit<RadixDataListRootProps, 'size'> &
  BaseDataListRootProps;
export type DataListLabelProps = RadixDataListLabelProps;
export type DataListValueProps = RadixDataListValueProps;

// DataListItem can be used with label/value props or as a wrapper
interface DataListItemWithProps {
  label: React.ReactNode;
  value: React.ReactNode;
  children?: never;
}

export type DataListItemProps = RadixDataListItemProps &
  (DataListItemWithProps | { label?: never; value?: never });

// Simple API props
export interface DataListItemRegular {
  label: React.ReactNode;
  value: React.ReactNode; // Can be string, number, or JSX component
  type?: never;
}

export interface DataListItemSeparator {
  type: 'separator';
  label?: never;
  value?: never;
  // Optional separator props
  orientation?: SeparatorProps['orientation'];
  color?: SeparatorProps['color'];
  size?: SeparatorProps['size'];
}

export type DataListItem = DataListItemRegular | DataListItemSeparator;

interface DataListSimpleProps
  extends Omit<RadixDataListRootProps, 'size' | 'orientation'> {
  items: DataListItem[];
  size?: DataListSize;
  orientation?: RadixDataListRootProps['orientation'];
  alignValues?: DataListValueAlignment;
  children?: never;
}

interface DataListContainerProps
  extends Omit<RadixDataListRootProps, 'size' | 'orientation'> {
  items?: never;
  children: React.ReactNode;
  size?: DataListSize;
  orientation?: RadixDataListRootProps['orientation'];
  alignValues?: DataListValueAlignment;
}

export type DataListProps =
  | DataListSimpleProps
  | DataListContainerProps
  | DataListRootProps;

const sizeMapping = {
  xsmall: '1',
  small: '1',
  medium: '2',
  large: '3',
} as const;

// Simple API DataList component
const DataList = forwardRef<HTMLDListElement, DataListProps>((props, ref) => {
  // Check if it's simple API (has items prop)
  const isSimpleAPI = 'items' in props && Array.isArray(props.items);

  if (isSimpleAPI) {
    const {
      items,
      size = 'small',
      orientation,
      alignValues,
      className,
      ...rest
    } = props as DataListSimpleProps;

    const radixSize = sizeMapping[size];
    const isHorizontal = orientation === 'horizontal';
    const shouldAlignValues = alignValues === 'end' && isHorizontal;

    return (
      <DataListPrimitive.Root
        ref={ref}
        size={radixSize}
        orientation={orientation}
        className={clsx(
          'data-list',
          `data-list-size-${size}`,
          shouldAlignValues && 'data-list-align-values-end',
          className
        )}
        style={
          shouldAlignValues
            ? {
                gridTemplateColumns: '1fr auto',
                ...rest.style,
              }
            : rest.style
        }
        {...rest}
      >
        {items.map((item, index) => {
          // Handle separator items
          if ('type' in item && item.type === 'separator') {
            // Separators between rows should always be horizontal
            // The DataList orientation refers to label/value layout, not row separation
            const separatorOrientation = item.orientation || 'horizontal';

            return (
              <Separator
                key={`separator-${index}`}
                orientation={separatorOrientation}
                color={item.color}
                size={item.size || 'full'}
                style={{
                  gridColumn: '1 / -1',
                  width: '100%',
                  marginTop: 'var(--space-2)',
                  marginBottom: 'var(--space-2)',
                }}
              />
            );
          }

          // Handle regular items
          return (
            <DataListPrimitive.Item key={index}>
              <DataListPrimitive.Label>{item.label}</DataListPrimitive.Label>
              <DataListPrimitive.Value>{item.value}</DataListPrimitive.Value>
            </DataListPrimitive.Item>
          );
        })}
      </DataListPrimitive.Root>
    );
  }

  // Container API - has children but no items
  const hasChildren = 'children' in props && props.children !== undefined;
  if (hasChildren && !('items' in props)) {
    const {
      children,
      size = 'small',
      orientation,
      alignValues,
      className,
      ...rest
    } = props as DataListContainerProps;

    const radixSize = sizeMapping[size];
    const isHorizontal = orientation === 'horizontal';
    const shouldAlignValues = alignValues === 'end' && isHorizontal;

    return (
      <DataListPrimitive.Root
        ref={ref}
        size={radixSize}
        orientation={orientation}
        className={clsx(
          'data-list',
          `data-list-size-${size}`,
          shouldAlignValues && 'data-list-align-values-end',
          className
        )}
        style={
          shouldAlignValues
            ? {
                gridTemplateColumns: '1fr auto',
                ...rest.style,
              }
            : rest.style
        }
        {...rest}
      >
        {children}
      </DataListPrimitive.Root>
    );
  }

  // Advanced API - render DataListRoot
  const rootProps = props as DataListRootProps;
  const { size = 'small', className, ...rest } = rootProps;

  const radixSize = sizeMapping[size];

  return (
    <DataListPrimitive.Root
      ref={ref}
      size={radixSize}
      className={clsx('data-list', `data-list-size-${size}`, className)}
      {...rest}
    />
  );
});

// Advanced API components
const DataListRoot = forwardRef<HTMLDListElement, DataListRootProps>(
  ({ size = 'small', alignValues, className, ...props }, ref) => {
    const radixSize = sizeMapping[size];
    const isHorizontal = props.orientation === 'horizontal';
    const shouldAlignValues = alignValues === 'end' && isHorizontal;

    return (
      <DataListPrimitive.Root
        ref={ref}
        size={radixSize}
        className={clsx(
          'data-list',
          `data-list-size-${size}`,
          shouldAlignValues && 'data-list-align-values-end',
          className
        )}
        style={
          shouldAlignValues
            ? {
                gridTemplateColumns: '1fr auto',
                ...props.style,
              }
            : props.style
        }
        {...props}
      />
    );
  }
);

const DataListItem = forwardRef<HTMLDivElement, DataListItemProps>(
  (props, ref) => {
    // If label and value are provided, render them automatically
    if (
      'label' in props &&
      'value' in props &&
      props.label !== undefined &&
      props.value !== undefined
    ) {
      const { label, value, ...rest } = props;
      return (
        <DataListPrimitive.Item ref={ref} {...rest}>
          <DataListPrimitive.Label>{label}</DataListPrimitive.Label>
          <DataListPrimitive.Value>{value}</DataListPrimitive.Value>
        </DataListPrimitive.Item>
      );
    }

    // Otherwise, render as a wrapper (advanced API)
    return <DataListPrimitive.Item ref={ref} {...props} />;
  }
);

const DataListLabel = forwardRef<HTMLDivElement, DataListLabelProps>(
  (props, ref) => {
    return <DataListPrimitive.Label ref={ref} {...props} />;
  }
);

const DataListValue = forwardRef<HTMLDivElement, DataListValueProps>(
  (props, ref) => {
    return <DataListPrimitive.Value ref={ref} {...props} />;
  }
);

DataList.displayName = 'DataList';
DataListRoot.displayName = 'DataListRoot';
DataListItem.displayName = 'DataListItem';
DataListLabel.displayName = 'DataListLabel';
DataListValue.displayName = 'DataListValue';

// Add subcomponents to DataList for backward compatibility
const DataListWithSubcomponents = DataList as typeof DataList & {
  Root: typeof DataListRoot;
  Item: typeof DataListItem;
  Label: typeof DataListLabel;
  Value: typeof DataListValue;
};

DataListWithSubcomponents.Root = DataListRoot;
DataListWithSubcomponents.Item = DataListItem;
DataListWithSubcomponents.Label = DataListLabel;
DataListWithSubcomponents.Value = DataListValue;

// Export both simple API and advanced API
export {
  DataListWithSubcomponents as DataList,
  DataListRoot,
  DataListItem,
  DataListLabel,
  DataListValue,
};
